import { AppFrameComponent } from '@app/app-frame.component';
import { AppRoutes } from '@app/app-routing.interface';
import { AppGuard } from '@app/app.guard';
import { authGuard } from '@app/auth/auth.guard';
import { copadoAdminGuard } from '@app/auth/copado-admin.guard';
import { homePageGuard } from '@app/auth/home-page.guard';
import { tokenGuard } from '@app/auth/token/token.guard';
import { projectRoutes } from '@app/projects/project.routes';
import { Feature } from '@app/shared/can-use.service';
import { currentOrgGuard } from '@app/shared/current-org.guard';
import { canOrgGuard } from '@app/shared/guards/can-guard';
import { crossDomainReturnToGuard } from '@app/shared/guards/cross-domain-return-to.guard';
import { projectGuard } from '@app/shared/guards/project.guard';
import { signinGuard } from '@app/shared/guards/signin.guard';
import { redirectTo } from '@app/shared/redirect-to';

const appRoutes: AppRoutes = [
  {
    canActivate: [
      authGuard,
      crossDomainReturnToGuard,
      projectGuard(),
      currentOrgGuard,
      homePageGuard,
    ],
    data: { title: 'Home' },
    loadComponent: () => import('./homepage/homepage.component').then((m) => m.HomepageComponent),
    path: '',
    pathMatch: 'full',
    runGuardsAndResolvers: 'always',
  },
  {
    canActivate: [authGuard, projectGuard(), currentOrgGuard],
    data: { title: 'Video Stream' },
    loadComponent: () =>
      import('./video-stream/video-stream.component').then((m) => m.VideoStreamComponent),
    path: 'videostream',
    pathMatch: 'full',
  },

  // These routes don't need to be guarded.
  {
    data: { title: 'About' },
    loadComponent: () => import('./about/about.component').then((m) => m.AboutComponent),
    path: 'about',
    pathMatch: 'full',
  },
  {
    path: 'accept',
    pathMatch: 'full',
    redirectTo: redirectTo({ url: ['/u/accept'] }),
  },
  {
    loadComponent: () => import('./releases/releases.component').then((m) => m.ReleasesComponent),
    path: 'releases',
    pathMatch: 'full',
    title: 'Releasees',
  },
  {
    canActivate: [authGuard, projectGuard(), currentOrgGuard],
    data: { title: 'Search' },
    loadComponent: () => import('./search/search.component').then((m) => m.SearchComponent),
    path: 'search',
    pathMatch: 'full',
  },

  /**
   * On-premises. (Formerly QConnect/PaceConnect).
   * This module is lazy-loaded. The routing is defined in `pace-connect-routing.module.ts`.
   * It has has a couple of redirects for backward compatibility.
   */
  {
    path: 'paceconnect',
    redirectTo: 'onprem',
  },
  {
    path: 'qconnect',
    redirectTo: 'onprem',
  },
  {
    canActivate: [authGuard, projectGuard(), currentOrgGuard],
    data: {
      defaultTitle: 'Connect Agent · On-premises',
    },
    loadChildren: () =>
      import('./pace-connect/pace-connect-routing.module').then((m) => m.paceConnectRoutes),
    path: 'onprem',
  },

  {
    canActivate: [authGuard, canOrgGuard(Feature.TestAuthoring), projectGuard(), currentOrgGuard],
    data: { title: $localize`Quick Start` },
    loadComponent: () =>
      import('./quickstart/quick-start-page.component').then((m) => m.QuickStartPageComponent),
    path: 'quickstart',
    pathMatch: 'full',
  },

  /**
   * Projects
   */
  ...projectRoutes,
  {
    path: 'dashboard',
    redirectTo: '/',
  },
  {
    canActivate: [authGuard, projectGuard('projectId', '/jobs'), currentOrgGuard],
    data: { title: 'Test Jobs', preload: Feature.TestAuthoring, projectChangeUrl: ['/jobs'] },
    loadChildren: () => import('@app/3.0/jobs/job.routes').then((m) => m.JOB_ROUTES),
    path: 'jobs',
    runGuardsAndResolvers: 'always',
  },
  {
    canActivate: [
      authGuard,
      canOrgGuard(Feature.DataTables),
      projectGuard('projectId', '/data-tables'),
      currentOrgGuard,
    ],
    data: {
      title: 'Data Tables',
      projectChangeUrl: ['/data-tables'],
    },
    loadChildren: () =>
      import('@app/3.0/data-tables/data-tables.routes').then((m) => m.DATA_TABLES_ROUTES),
    path: 'data-tables',
    runGuardsAndResolvers: 'always',
  },
  {
    canActivate: [authGuard, projectGuard('projectId', '/runs'), currentOrgGuard],
    data: { title: 'Test Runs', preload: Feature.TestAuthoring },
    loadChildren: () => import('@app/test-runs/test-runs.routes').then((m) => m.TEST_RUNS_ROUTES),
    path: 'runs',
    runGuardsAndResolvers: 'always',
  },
  // Old QEditor route for which we need a redirect.
  {
    path: 'jobs/:jobId/editor',
    redirectTo: redirectTo({
      url: ['/editor'],
      map: {
        jobId: 'jobId',
      },
    }),
  },
  // New Qeditor route.
  {
    canActivate: [authGuard, projectGuard('projectId', '/projects'), currentOrgGuard],
    data: {
      projectChangeUrl: ['/editor'],
      preload: Feature.TestAuthoring,
      minimalQueryParamsChange: true,
    },
    loadChildren: () => import('@app/qeditor/qeditor.routes').then((m) => m.qEditorRoutes),
    path: 'editor',
    runGuardsAndResolvers: 'always',
  },
  {
    canActivate: [authGuard, projectGuard('projectId', '/robots'), currentOrgGuard],
    data: { preload: Feature.TestAuthoring },
    loadChildren: () => import('@app/3.0/robots/robot.routes').then((m) => m.ROBOT_ROUTES),
    path: 'robots',
    runGuardsAndResolvers: 'always',
  },
  {
    path: 'insights',
    redirectTo: 'analytics',
  },
  /**
   * Analytics - lazy loaded
   */
  {
    canActivate: [authGuard, projectGuard(), currentOrgGuard],
    data: {
      defaultTitle: 'Quality Intelligence',
    },
    loadChildren: () => import('./insights/insights.routes').then((m) => m.INSIGHTS_ROUTES),
    loadComponent: () =>
      import('./insights/insights-nav/insights-nav.component').then((m) => m.InsightsNavComponent),
    path: 'analytics',
  },

  /**
   * Copado Explorer
   */
  {
    canActivate: [authGuard, projectGuard()],
    data: { defaultTitle: 'Copado Explorer', preload: Feature.Explorer },
    loadChildren: () => import('@app/explorer/routes').then((m) => m.OLD_RE_ROUTES),
    path: 'explorer',
  },
  {
    canActivate: [authGuard, projectGuard(), currentOrgGuard],
    data: { defaultTitle: 'Copado Explorer', preload: Feature.Explorer },
    loadChildren: () => import('@app/explorer/routes').then((m) => m.RE_ROUTES),
    path: 'explorations',
    runGuardsAndResolvers: 'always',
  },

  /**
   * Insights on Pace (QI for DevOps) - lazy loaded
   */
  {
    canActivate: [authGuard, projectGuard(), currentOrgGuard],
    data: {
      defaultTitle: 'Quality Intelligence',
    },
    loadChildren: () => import('./insights/insights.routes').then((m) => m.QI_ROUTES),
    loadComponent: () =>
      import('./insights/insights-nav/insights-nav.component').then((m) => m.InsightsNavComponent),
    path: 'qi',
  },

  /**
   * Orgs - lazy loaded via orgsRoutes (which now uses loadComponent internally)
   */
  {
    canActivate: [authGuard, projectGuard(), currentOrgGuard],
    data: { defaultTitle: 'Organizations' },
    loadChildren: () => import('./orgs/orgs-routes').then((m) => m.orgsRoutes),
    path: 'orgs',
    runGuardsAndResolvers: 'always',
  },

  /**
   * Settings - lazy loaded
   */
  {
    path: 'settings',
    canActivate: [authGuard, projectGuard(), currentOrgGuard],
    loadChildren: () => import('./settings/settings.routes').then((m) => m.SETTINGS_ROUTES),
  },
  {
    canActivate: [authGuard, copadoAdminGuard, projectGuard(), currentOrgGuard],
    data: { defaultTitle: 'Admin' },
    loadChildren: () => import('./admin/admin.routes').then((m) => m.adminRoutes),
    path: 'ca', // Obscure on purpose to avoid accidental access.
  },
  {
    loadComponent: () =>
      import('./page-not-found/page-not-found.component').then((m) => m.PageNotFoundComponent),
    data: { title: '404 Not Found' },
    path: '**',
  },
];

export const routes: AppRoutes = [
  {
    path: 'u',
    loadChildren: () => import('./auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    canActivate: [signinGuard],
    loadComponent: () =>
      import('./page-not-found/page-not-found.component').then((m) => m.PageNotFoundComponent),
    path: 'signin/:provider',
    pathMatch: 'full',
  },
  {
    path: 'ai',
    children: [
      {
        path: '**',
        loadComponent: () => import('@app/reload/reload.component').then((m) => m.ReloadComponent),
        data: {
          queryParamsToKeep: ['orgId'],
        },
      },
    ],
  },
  {
    path: 'tango',
    children: [
      {
        path: '**',
        loadComponent: () => import('@app/reload/reload.component').then((m) => m.ReloadComponent),
      },
    ],
  },
  { path: 'accept', redirectTo: 'u/accept' },
  { path: 'login', redirectTo: 'u/login' },
  { path: 'logout', redirectTo: 'u/logout' },
  { path: 'reset', redirectTo: 'u/reset' },
  { path: 'recover', redirectTo: 'u/recover' },
  { path: 'signup', redirectTo: 'u/signup' },
  { path: 'verify', redirectTo: 'u/verify' },
  { path: 'azure-ad/consent', redirectTo: 'u/azure-consent' },
  {
    path: 'azure-ad/token',
    canActivate: [tokenGuard],
    loadComponent: () => import('./shared/empty/empty.component').then((m) => m.EmptyComponent),
  },
  {
    path: 'okta/token',
    canActivate: [tokenGuard],
    loadComponent: () => import('./shared/empty/empty.component').then((m) => m.EmptyComponent),
  },
  {
    path: 'google/token',
    canActivate: [tokenGuard],
    loadComponent: () => import('./shared/empty/empty.component').then((m) => m.EmptyComponent),
  },
  {
    path: 'salesforce/token',
    canActivate: [tokenGuard],
    loadComponent: () => import('./shared/empty/empty.component').then((m) => m.EmptyComponent),
  },
  {
    canActivate: [AppGuard, currentOrgGuard],
    data: {
      routeOnError: '/embedded/not-found',
    },
    loadComponent: () =>
      import('./embedded/analytics/dashboards/dashboards.component').then(
        (mod) => mod.DashboardsComponent,
      ),
    path: 'embedded/analytics/dashboards',
    pathMatch: 'full',
  },
  {
    loadComponent: () => import('./about/about.component').then((m) => m.AboutComponent),
    path: 'embedded/about',
    pathMatch: 'full',
  },
  {
    data: {
      showLinks: false,
    },
    loadComponent: () =>
      import('./page-not-found/page-not-found.component').then((m) => m.PageNotFoundComponent),
    path: 'embedded/not-found',
    pathMatch: 'full',
  },
  {
    canActivate: [AppGuard],
    children: appRoutes,
    component: AppFrameComponent,
    path: '',
    runGuardsAndResolvers: 'always',
  },
];
